import {
  TimelineEvent,
  VisibleTimelineEvent,
  RenderedTimelineEvent,
} from '../components/CaptureViewer/ViewerPlaybackControls';
import {
  displayToCoordinate,
  TIMELINE_EVENT_MARKER_WIDTH_IN_PX,
} from '../components/CaptureViewer/ViewerPlaybackControls/constants';

// Function to group close markers together together
export function renderTimelineEvents(
  timelineEvents: Array<RenderedTimelineEvent>,
): Array<RenderedTimelineEvent> {
  const timelineEventWidthInSeconds = displayToCoordinate(
    TIMELINE_EVENT_MARKER_WIDTH_IN_PX,
  );

  // Initialize variables
  type WorkingRenderedTimelineValues = {
    renderedTimelineEvents: Array<RenderedTimelineEvent>;
    nextVisibleFilledEventSecond: number;
    nextVisibleHollowEventSecond: number;
    lastVisibleFilledEventIndex?: number;
    lastVisibleHollowEventIndex?: number;
  };
  const initialValues: WorkingRenderedTimelineValues = {
    renderedTimelineEvents: timelineEvents,
    nextVisibleFilledEventSecond: 0,
    nextVisibleHollowEventSecond: 0,
  };

  // Function to execute at each step in the event array
  // This checks whether the current event is closed to a previous event of the same type
  // and if so, add a flag to not render it
  const convertTimelineEvents = (
    workingValues: WorkingRenderedTimelineValues,
    event: TimelineEvent,
    index: number,
  ): WorkingRenderedTimelineValues => {
    let {
      renderedTimelineEvents,
      nextVisibleFilledEventSecond,
      nextVisibleHollowEventSecond,
      lastVisibleFilledEventIndex = null,
      lastVisibleHollowEventIndex = null,
    } = workingValues;

    const returnWorkingValues = () => {
      return {
        renderedTimelineEvents,
        nextVisibleFilledEventSecond,
        nextVisibleHollowEventSecond,
        lastVisibleFilledEventIndex,
        lastVisibleHollowEventIndex,
      };
    };

    // Checks if this is the first event of its filled type, and if so set it as the
    // most recent event of that filled type
    if (lastVisibleFilledEventIndex === null && event.filled) {
      lastVisibleFilledEventIndex = index;
      nextVisibleFilledEventSecond =
        event.secondsOffset + timelineEventWidthInSeconds;
      renderedTimelineEvents[index] = {
        ...event,
        visibleInTrack: true,
        label: 1,
      };
      return returnWorkingValues();
    }
    if (lastVisibleHollowEventIndex === null && !event.filled) {
      lastVisibleHollowEventIndex = index;
      nextVisibleHollowEventSecond =
        event.secondsOffset + timelineEventWidthInSeconds;
      renderedTimelineEvents[index] = {
        ...event,
        visibleInTrack: true,
        label: 1,
      };
      return returnWorkingValues();
    }

    // Generalize filled type
    const nextVisibleEventSecond = event.filled
      ? nextVisibleFilledEventSecond
      : nextVisibleHollowEventSecond;
    const lastEventOfSameFilledTypeIndex = event.filled
      ? lastVisibleFilledEventIndex
      : lastVisibleHollowEventIndex;

    // If this event occurred before the next possible visible event, then collapse it
    if (event.secondsOffset < nextVisibleEventSecond) {
      // don't show this event
      renderedTimelineEvents[index] = {
        ...event,
        visibleInTrack: false,
      };
      // increment the label for number of overlapping events
      renderedTimelineEvents[lastEventOfSameFilledTypeIndex] = {
        ...renderedTimelineEvents[lastEventOfSameFilledTypeIndex],
        visibleInTrack: true,
        label:
          (
            renderedTimelineEvents[
              lastEventOfSameFilledTypeIndex
            ] as VisibleTimelineEvent
          ).label + 1,
      };
      return returnWorkingValues();
    }

    // Otherwise, render this event as a separate event and recalculate all working values
    if (event.filled) {
      nextVisibleFilledEventSecond =
        event.secondsOffset + timelineEventWidthInSeconds;
      lastVisibleFilledEventIndex = index;
    } else {
      nextVisibleHollowEventSecond =
        event.secondsOffset + timelineEventWidthInSeconds;
      lastVisibleHollowEventIndex = index;
    }
    renderedTimelineEvents[index] = {
      ...event,
      visibleInTrack: true,
      label: 1,
    };

    return returnWorkingValues();
  };

  // Get a list of all ingresses and egresses for this track.
  const timelineEventsByDirection = { ingress: [], egress: [] };
  timelineEvents
    .sort((a, b) => a.secondsOffset - b.secondsOffset)
    .forEach((event) => {
      timelineEventsByDirection[
        event.direction === 1 ? 'ingress' : 'egress'
      ].push(event);
    });

  // Run each of those lists through the overlap calculation function.
  return [
    ...timelineEventsByDirection.ingress.reduce(
      (workingValues, event, index) =>
        convertTimelineEvents(workingValues, event, index),
      {
        ...initialValues,
        renderedTimelineEvents: timelineEventsByDirection.ingress,
      },
    ).renderedTimelineEvents,
    ...timelineEventsByDirection.egress.reduce(
      (workingValues, event, index) =>
        convertTimelineEvents(workingValues, event, index),
      {
        ...initialValues,
        renderedTimelineEvents: timelineEventsByDirection.egress,
      },
    ).renderedTimelineEvents,
  ];
}
